import React, { useCallback, useMemo, useState } from "react";
import plur from "plur";
import { Button, Flex, SegmentedControl, Text } from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import {
  useGetDistributionBatchForCanopyPaymentBatchCreationQuery,
  useGetPaymentsLpComplianceScreeningFeatureFlagQuery,
} from "../../../../graphql/supergraph.index";
import { CurrencyCode } from "../../../../graphql/supergraphOperations";
import { clientContext, toGlobalId } from "../../../../utils/graphql_utils";
import Money, { MoneyType } from "../../../Money";
import { addMoney, DEFAULT_PRECISION } from "../../../Wallet/utils";
import { useDistributionWorkflowContext } from "../../Context/context";
import { DistributionWorkflowStepProps } from "../../types";
import { SelectPositionsTable } from "./SelectPositionsTable";
import { PositionTabStatus } from "./SelectPositionsTable/constants";
import { PositionRowData } from "./SelectPositionsTable/types";
import {
  getPositionRowData,
  sortByPaymentInstructionsUpdated,
} from "./SelectPositionsTable/utils";
import {
  buttons,
  container,
  contentContainer,
  fieldLabel,
  footer,
  headerContainer,
  linkButton,
} from "./styles_vanilla.css";

const DEFAULT_TAB_STATUS = PositionTabStatus.Available;

const SelectPositionsStep: React.FC<DistributionWorkflowStepProps> = ({
  onBack,
  onContinue,
}: DistributionWorkflowStepProps) => {
  const { distributionBatchId: distributionBatchDbId, setSelectedPositions } =
    useDistributionWorkflowContext();
  const picrCasesEnabled = useFeatureFlag("picrCasesEnabled", false);
  const { data: distributionBatchData, loading: distributionBatchLoading } =
    useGetDistributionBatchForCanopyPaymentBatchCreationQuery({
      variables: {
        distributionBatchId: toGlobalId(
          "DistributionBatchObjectType",
          distributionBatchDbId || -1,
        ),
        picrCasesEnabled,
      },
      context: clientContext(),
      fetchPolicy: "no-cache",
    });

  const { data: featureFlagData, loading: featureFlagLoading } =
    useGetPaymentsLpComplianceScreeningFeatureFlagQuery({
      context: clientContext(),
    });

  const loading = distributionBatchLoading || featureFlagLoading;
  const lpComplianceScreeningEnabled =
    !!featureFlagData?.devFeatures?.paymentsLpComplianceScreening;

  const zeroAmount = useMemo<MoneyType>(
    () => ({
      amount: 0,
      currency:
        distributionBatchData?.distributionBatch?.entity?.currency.code || CurrencyCode.Usd,
      precision: DEFAULT_PRECISION,
    }),
    [distributionBatchData?.distributionBatch?.entity?.currency.code],
  );

  const [selectedSum, setSelectedSum] = useState<MoneyType>(zeroAmount);

  const positionRowData = useMemo<PositionRowData[]>(
    () =>
      getPositionRowData(
        distributionBatchData?.distributionBatch?.instances || [],
        distributionBatchData?.distributionBatch?.entity?.currency.code || CurrencyCode.Usd,
        lpComplianceScreeningEnabled,
      ).sort(sortByPaymentInstructionsUpdated),
    [
      distributionBatchData?.distributionBatch?.entity?.currency.code,
      distributionBatchData?.distributionBatch?.instances,
      lpComplianceScreeningEnabled,
    ],
  );

  const availableToPayPositions = useMemo(() => {
    return positionRowData.filter(
      (row) => row.positionTabStatus === PositionTabStatus.Available,
    );
  }, [positionRowData]);

  const paidPositions = useMemo(() => {
    return positionRowData.filter((row) => row.positionTabStatus === PositionTabStatus.Paid);
  }, [positionRowData]);

  const unableToPayPositions = useMemo(() => {
    return positionRowData.filter(
      (row) => row.positionTabStatus === PositionTabStatus.UnableToPay,
    );
  }, [positionRowData]);

  const [selectedPositionRows, setSelectedPositionRows] = useState<PositionRowData[]>([]);

  const onSelectionChange = useCallback(
    (selectedRows: PositionRowData[]) => {
      let newSum = [{ ...zeroAmount }];
      selectedRows.forEach((row) => {
        newSum = addMoney(newSum, [row.netPayment]);
      });
      setSelectedSum({ ...newSum[0] });
      setSelectedPositionRows(selectedRows);
    },
    [zeroAmount],
  );

  const selectedPositionsCount = selectedPositionRows.length;

  const [tabStatus, setTabStatus] = useState<PositionTabStatus>(DEFAULT_TAB_STATUS);
  const tabOptions = useMemo(
    () => [
      {
        value: PositionTabStatus.Available,
        label: `Available to pay (${availableToPayPositions.length})`,
      },
      {
        value: PositionTabStatus.UnableToPay,
        label: `Unable to pay (${unableToPayPositions.length})`,
      },
      {
        value: PositionTabStatus.Paid,
        label: `Paid (${paidPositions.length})`,
      },
    ],
    [availableToPayPositions.length, paidPositions.length, unableToPayPositions.length],
  );

  const handleContinue = useCallback(() => {
    setTabStatus(DEFAULT_TAB_STATUS);
    setSelectedPositions(selectedPositionRows);
    onContinue();
  }, [onContinue, selectedPositionRows, setSelectedPositions]);

  const handleBack = useCallback(() => {
    setTabStatus(DEFAULT_TAB_STATUS);
    onBack();
  }, [onBack]);

  return (
    <Flex className={container} flexDirection="column">
      <Flex className={headerContainer} justifyContent="space-between">
        <Text variant="h2" data-testid="select-positions-title">
          Positions
        </Text>
        <SegmentedControl
          onChange={(option: string) => {
            setTabStatus(option as PositionTabStatus);
          }}
          value={tabStatus}
          options={tabOptions}
        />
      </Flex>
      <Flex className={contentContainer} flexDirection="column">
        {tabStatus === PositionTabStatus.Available && (
          <SelectPositionsTable
            allowSelection={true}
            emptyTableMessage="You have no positions available to be paid"
            positionRowData={availableToPayPositions}
            handleSelectionChange={onSelectionChange}
            loading={loading}
          />
        )}
        {tabStatus === PositionTabStatus.Paid && (
          <SelectPositionsTable
            allowSelection={false}
            emptyTableMessage="You have no positions that have been paid"
            positionRowData={paidPositions}
            loading={loading}
          />
        )}
        {tabStatus === PositionTabStatus.UnableToPay && (
          <SelectPositionsTable
            allowSelection={false}
            emptyTableMessage="You have no positions that are unable to be paid"
            positionRowData={unableToPayPositions}
            loading={loading}
            showStatusColumn={true}
          />
        )}
      </Flex>
      <Flex className={footer} justifyContent="space-between">
        <Flex flexDirection="row">
          <Flex>
            <Button
              onClick={handleBack}
              data-cy="back-button"
              data-testid="back-button"
              variant="neutral"
              className={buttons}
            >
              Back
            </Button>
          </Flex>
        </Flex>
        <Flex flexDirection="row-reverse">
          <Flex>
            <Button
              onClick={handleContinue}
              data-cy="continue-button"
              data-testid="continue-button"
              className={buttons}
              disabled={selectedPositionsCount === 0}
            >
              Continue
            </Button>
          </Flex>
          <Flex
            alignItems="center"
            data-testid="positions-added-to-batch"
            data-cy="positions-added-to-batch"
          >
            <Flex>
              <Text className={fieldLabel}>
                <Button isInline displayAsLink className={linkButton}>
                  {`${selectedPositionsCount} ${plur("position", selectedPositionsCount)}`}
                </Button>{" "}
                totaling <Money money={selectedSum} rounded={false} /> have been added to this
                batch
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default SelectPositionsStep;
